<script lang="ts">
  import { mdiEyeOutline as standardDetailsIcon } from "@mdi/js"

  import { buildTableRowActionSet } from "@kontextwerk/web-sdk/core/layout/table"

  import { mdiDelete, mdiOpenInNew, mdiSelectionEllipseRemove } from "@mdi/js"
  import { getSortedTablePage } from "@kontextwerk/web-sdk/core/layout/tableSort"
  import { newApproval } from "@kontextwerk/web-sdk/core/overlay/approvals"
  import { openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import StatusTag from "@kontextwerk/web-sdk/ui/display/StatusTag.svelte"
  import TableFormCell from "@kontextwerk/web-sdk/ui/layout/TableFormCell.svelte"
  import Table, {
    type ColumnDef,
    type DataRow,
    type TableGetRows,
  } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import { spaNavigate } from "../../../../../../../../../../../../_shared/_logic/navigation"
  import {
    deletePropertyCustomer,
    updatePropertyCustomer,
  } from "../../../../../../../../../../../../api/kehrblatt/property-customer-registry/property-customers"
  import {
    formatAddressInline,
    formatAddressLines,
  } from "../../../../../../../../../../../../_shared/_logic/countries"
  import {
    formatPropertyCustomerRole,
    getPropertyCustomerRoleStatus,
    normalizePropertyCustomerRole,
    outreachEnabledToSelectValue,
    propertyCustomerOutreachOptions,
    propertyCustomerRoleOptions,
    selectValueToOutreachEnabled,
    type PropertyCustomerOutreachValue,
  } from "../../../../../../../../../../_shared/_logic/customer-roles"
  import {
    CUSTOMER_REGISTRY_ACTION_TOOLTIP_CLASS,
    customerRegistryActionTooltips,
  } from "../../../../../../../../_shared/_logic/customer-registry-action-tooltips"
  import { formatPropertyUnitDisplay } from "../../../../../../../../../../../../_shared/_logic/unit-labels"
  import CustomerUnitSelectionOverlay from "./_modules/customer-unit-selection-overlay"

  type Props = {
    organizationId: string
    customer: Customer
    properties?: Property[]
    propertyLinks?: PropertyCustomer[]
    canEdit?: boolean
    onChanged?: (payload: { properties: Property[]; links: PropertyCustomer[] }) => void
  }

  type CustomerUnitRow = DataRow & {
    link: PropertyCustomer
    property: Property | undefined
    unit: PropertyUnit | undefined
    unitLabel: string
    addressLabel: string
    addressLines: string[]
    roleValue: PropertyCustomerRole
    roleLabel: string
    outreachValue: PropertyCustomerOutreachValue
    outreachLabel: string
  }

  let {
    organizationId,
    customer,
    properties = [],
    propertyLinks = [],
    canEdit = true,
    onChanged = () => {},
  }: Props = $props()

  const columns: ColumnDef[] = [
    {
      id: "unitLabel",
      label: "Einheit",
      isPrimary: true,
      sortable: true,
      sortKey: "unitLabel",
      sortType: "text",
    },
    {
      id: "addressLabel",
      label: "Liegenschaft",
      sortable: true,
      sortKey: "addressLabel",
      sortType: "text",
    },
    { id: "roleLabel", label: "Rolle", sortable: true, sortKey: "roleLabel", sortType: "text" },
    {
      id: "outreachLabel",
      label: "Darf angesprochen werden",
      sortable: true,
      sortKey: "outreachLabel",
      sortType: "text",
    },
  ]

  const propertyById = $derived(
    Object.fromEntries((properties ?? []).map((property) => [property.id, property])),
  )

  const resolveUnit = (property: Property | undefined, link: PropertyCustomer) =>
    (property?.units ?? []).find((unit) => unit.id === link.property_unit_id)

  const formatUnitLabel = (property: Property | undefined, link: PropertyCustomer) => {
    const unit = resolveUnit(property, link)

    if (unit) return formatPropertyUnitDisplay(unit)

    return link.property_unit_id ? "Unbekannte Wohneinheit" : "Gesamte Liegenschaft"
  }

  const rows = $derived.by<CustomerUnitRow[]>(() =>
    (propertyLinks ?? [])
      .filter((link) => link.customer_id === customer.id)
      .map((link) => {
        const property = propertyById[link.property_id]
        const unit = resolveUnit(property, link)
        const addressLines = property ? formatAddressLines(property) : ["Unbekannte Liegenschaft"]

        return {
          id: link.id,
          link,
          property,
          unit,
          unitLabel: formatUnitLabel(property, link),
          addressLabel: property
            ? formatAddressInline(property) || "Ohne Adresse"
            : "Unbekannte Liegenschaft",
          addressLines,
          roleValue: normalizePropertyCustomerRole(link.role),
          roleLabel: formatPropertyCustomerRole(link.role),
          outreachValue: outreachEnabledToSelectValue(link.outreach_enabled),
          outreachLabel: link.outreach_enabled === false ? "Nein" : "Ja",
        }
      })
      .sort((left, right) => {
        const addressCompare = left.addressLabel.localeCompare(right.addressLabel, "de")

        if (addressCompare !== 0) return addressCompare

        return left.unitLabel.localeCompare(right.unitLabel, "de")
      }),
  )

  const refreshKey = $derived(
    rows.map((row) => `${row.id}:${row.roleLabel}:${row.outreachLabel}`).join("|"),
  )
  const title = $derived(`Einheiten (${rows.length})`)

  const getRows: TableGetRows = (params) =>
    getSortedTablePage({
      rows,
      columns,
      sort: (params as { sort?: unknown }).sort as any,
      limit: params.limit,
      offset: params.offset,
    })

  const emitChanged = () => onChanged?.({ properties, links: propertyLinks })

  const mergeProperties = (base: Property[], incoming: Property[]) => {
    const byId = new Map(base.map((property) => [property.id, property]))
    for (const property of incoming ?? []) {
      byId.set(property.id, property)
    }

    return Array.from(byId.values())
  }

  const handleLinked = (payload: { links: PropertyCustomer[]; properties: Property[] }) => {
    propertyLinks = [...payload.links, ...propertyLinks]
    properties = mergeProperties(properties, payload.properties)
    emitChanged()
  }

  const openSelectionOverlay = () => {
    if (!canEdit || !organizationId || !customer?.id) return

    openOverlay({
      title: "Einheiten auswählen",
      content: CustomerUnitSelectionOverlay,
      properties: {
        organizationId,
        customerId: customer.id,
        properties,
        propertyLinks,
        onLinked: handleLinked,
      },
    })
  }

  const updateLinkRole = async (row: CustomerUnitRow, role: PropertyCustomerRole) => {
    if (!canEdit) return

    try {
      const updated = await updatePropertyCustomer(row.link.id, { role })
      propertyLinks = propertyLinks.map((link) =>
        link.id === row.link.id ? { ...link, ...updated } : link,
      )
      emitChanged()
      newNotification({ class: "success", html: "Rolle gespeichert." })
    } catch (err) {
      console.error("customer_unit_role_update_failed", err)
      newNotification({ class: "error", html: "Rolle konnte nicht gespeichert werden." })
    }
  }

  const updateLinkOutreach = async (row: CustomerUnitRow, value: PropertyCustomerOutreachValue) => {
    if (!canEdit) return

    try {
      const updated = await updatePropertyCustomer(row.link.id, {
        outreach_enabled: selectValueToOutreachEnabled(value),
      })
      propertyLinks = propertyLinks.map((link) =>
        link.id === row.link.id ? { ...link, ...updated } : link,
      )
      emitChanged()
      newNotification({ class: "success", html: "Ansprechen gespeichert." })
    } catch (err) {
      console.error("customer_unit_outreach_update_failed", err)
      newNotification({ class: "error", html: "Ansprechen konnte nicht gespeichert werden." })
    }
  }

  const openPropertyUnitsPage = (row: CustomerUnitRow) => {
    const propertyId = row.property?.id ?? row.link.property_id

    if (!organizationId || !propertyId) return

    spaNavigate(
      `/organizations/${encodeURIComponent(organizationId)}/addresses/${encodeURIComponent(propertyId)}/units`,
      { noSearchParam: false },
    )
  }

  const roleTooltipForUnit = (row: CustomerUnitRow) =>
    `Diese Rolle gilt für ${[row.unitLabel, row.addressLabel].filter(Boolean).join(", ")}.`

  const removeLink = (row: CustomerUnitRow) => {
    if (!canEdit) return

    newApproval({
      title: "Zuordnung entfernen?",
      description:
        "Der Ansprechpartner bleibt erhalten, wird aber von dieser Einheit getrennt und für diese Einheit nicht mehr automatisch angesprochen.",
      approvalLabel: "Entfernen",
      declineLabel: "Abbrechen",
      approvalCallback: async () => {
        try {
          await deletePropertyCustomer(row.link.id)
          propertyLinks = propertyLinks.filter((link) => link.id !== row.link.id)
          emitChanged()
          newNotification({ class: "success", html: "Zuordnung entfernt." })
        } catch (err) {
          console.error("customer_unit_link_delete_failed", err)
          newNotification({ class: "error", html: "Zuordnung konnte nicht entfernt werden." })
        }
      },
    })
  }
</script>

<SectionTitle {title} styleOuter="margin-bottom: 1.2rem;">
  {#snippet buttonSnippet()}
    {#if canEdit}
      <CTA
        type="secondaryButton"
        content="Auswählen"
        tooltipContent={customerRegistryActionTooltips.assignContactsToUnit}
        tooltipClassName={CUSTOMER_REGISTRY_ACTION_TOOLTIP_CLASS}
        onClick={openSelectionOverlay}
      >
        {#snippet iconSnippet()}
          <Icon path={mdiSelectionEllipseRemove} />
        {/snippet}
      </CTA>
    {/if}
  {/snippet}
</SectionTitle>

<Table
  rowActions={{
    resolve: (row, rowIndex) =>
      buildTableRowActionSet(
        ((row) => {
          const actions: import("@kontextwerk/web-sdk/core/layout/table").TableRowAction[] = []

          const unitRow = row as CustomerUnitRow

          actions.push({
            id: "action-0",
            label: "Liegenschaft öffnen",
            onClick: () => {
              openPropertyUnitsPage(unitRow)
            },
            icon: standardDetailsIcon,
            tooltip: "Liegenschaft öffnen",
          })

          if (canEdit) {
            actions.push({
              id: "action-1",
              label: "Zuordnung entfernen",
              onClick: () => {
                removeLink(unitRow)
              },
              icon: mdiDelete,
              disabled: !canEdit,
              tooltip: customerRegistryActionTooltips.removeUnitContactAssignment,
              tone: "danger",
            })
          }

          return actions
        })(row) ?? [],
        { primaryId: "action-0", inlineIds: [] },
      ),
  }}
  {columns}
  {getRows}
  {refreshKey}
  theme="light"
  urlStateId={`kehrblatt-customer-units-${customer.id}`}
  mode="table"
  showActions={true}
  actionsHeaderLabel="Aktionen"
>
  {#snippet tableCell({ row, column })}
    {@const unitRow = row as CustomerUnitRow}
    {#if column.id === "roleLabel"}
      {#if canEdit}
        <TableFormCell style="width: min(100%, 18rem);">
          <Input
            type="select"
            placeholder="Rolle"
            labelVisibility="hidden"
            options={propertyCustomerRoleOptions}
            bind:value={unitRow.roleValue}
            brightBackground={true}
            onChange={() => updateLinkRole(unitRow, unitRow.roleValue)}
          />
        </TableFormCell>
      {:else}
        <StatusTag
          variant="neutral"
          size="sm"
          status={getPropertyCustomerRoleStatus(unitRow.roleValue)}
          tooltipContent={roleTooltipForUnit(unitRow)}
        />
      {/if}
    {:else if column.id === "outreachLabel"}
      {#if canEdit}
        <TableFormCell style="width: min(100%, 18rem);">
          <Input
            type="select"
            placeholder="Darf angesprochen werden"
            labelVisibility="hidden"
            options={propertyCustomerOutreachOptions}
            bind:value={unitRow.outreachValue}
            brightBackground={true}
            onChange={() => updateLinkOutreach(unitRow, unitRow.outreachValue)}
          />
        </TableFormCell>
      {:else}
        {unitRow.outreachLabel}
      {/if}
    {:else if column.id === "addressLabel"}
      <span class="address-cell">
        {#each unitRow.addressLines as line, idx (`${unitRow.id}-address-${idx}`)}
          <span class="address-cell__line">{line}</span>
        {/each}
      </span>
    {:else}
      {(row as any)[column.id] ?? ""}
    {/if}
  {/snippet}

  {#snippet empty()}
    <div class="empty-state">Noch keine Einheiten zugeordnet.</div>
  {/snippet}
</Table>

<style lang="less">
  .empty-state {
    padding: 0.4rem 0;
    color: var(--text-invers-100);
  }

  .address-cell {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    max-width: 100%;
    line-height: 1.25;
  }

  .address-cell__line {
    min-width: 0;
    max-width: 100%;
    overflow-wrap: anywhere;
  }
</style>
